// @flow
import React, {PureComponent, Fragment} from 'react';
import classnames from 'classnames';
import {Dropdown} from '@homeaway/react-dropdown';
import {Query} from 'react-apollo';
import {FormattedMessage} from 'react-intl';
import {MULTIPLE_UNITS_DROPDOWN_HEIGHT} from '../../../utils/constants';
import {
    createMultiUnitSearchQuery,
    setVariables,
    setQueryStartTimer,
    setQueryEndTimer,
    clearQueryTimer
} from '../utils/MultiUnitQuery.util';
import MultiUnitTypesSummary from '../MultiUnitTypesSummary/MultiUnitTypesSummary';
import MultiUnitLoader from '../MultiUnitLoader/MultiUnitLoader';
import MultiUnitListing from '../MultiUnitListing/MultiUnitListing';

import logger from '../../../utils/clientLogger';
import {triggerMultipleUnitsClickEDAPEvent} from '../../../utils/hitEdapUtils';
import Timing from '../../Timing/Timing';
import {response} from '../Mocks/MultiUnitSearchResponse';

import type {SearchParamsType} from '../../../reducers/searchParams';
import './MultiUnitDropdown.less';

type Props = {
    inputParams: SearchParamsType,
    systemId: string,
    propertyId: number,
    propertyName: string,
    currency: string,
    isDatelessSearch: boolean,
    isMetric: boolean,
    fullLocale: string,
    mock: string
};

type State = {
    isOpen: boolean
};

const MULTI_UNIT_LABEL = (
    <FormattedMessage
        id="multipleUnitsButtonLabel"
        defaultMessage="Multiple units"
    />
);

export default class MultiUnitDropdown extends PureComponent<Props, State> {
    state = {
        isOpen: false
    };

    onDropdownShow = () => {
        triggerMultipleUnitsClickEDAPEvent(this.props);
        this.setState({isOpen: true});
        setQueryStartTimer('multiUnitSearchQuery_desktop');
    };

    onDropdownHide = () => {
        this.setState({isOpen: false});
        clearQueryTimer('multiUnitSearchQuery_desktop');
    };

    render() {
        const {isOpen} = this.state;
        const {
            currency,
            fullLocale,
            inputParams,
            isDatelessSearch,
            isMetric,
            mock,
            propertyId,
            propertyName,
            systemId
        } = this.props;

        const variables = setVariables(inputParams, systemId, propertyId);

        const dropdownClass = classnames('MultiUnit__dropdown', {
            MultiUnitDropdown__Open: isOpen
        });

        return (
            <div className="MultiUnitDropdown" aria-label={propertyName}>
                <Dropdown
                    className={dropdownClass}
                    maxHeight={MULTIPLE_UNITS_DROPDOWN_HEIGHT}
                    label={MULTI_UNIT_LABEL}
                    id="multi-unit-dropdown"
                    dropdownRight
                    open={isOpen}
                    onDropdownShow={this.onDropdownShow}
                    onDropdownHide={this.onDropdownHide}
                >
                    {isOpen &&
                        ((mock === 'withKitchenSink' && (
                            <div>
                                <MultiUnitTypesSummary
                                    propertyName={propertyName}
                                    resultCount={
                                        response.data &&
                                        response.data.multiUnitSearch &&
                                        response.data.multiUnitSearch
                                            .resultCount
                                    }
                                    loading={false}
                                />
                                <MultiUnitListing
                                    context="dropdown"
                                    fullLocale={fullLocale}
                                    isDatelessSearch={isDatelessSearch}
                                    isMetric={isMetric}
                                    multiUnitList={response.data}
                                    propertyId={propertyId}
                                />
                            </div>
                        )) || (
                            <Query
                                query={createMultiUnitSearchQuery(currency)}
                                variables={variables}
                                errorPolicy="all"
                            >
                                {({loading, error, data}) => {
                                    if (loading) {
                                        return (
                                            <Fragment>
                                                <MultiUnitTypesSummary
                                                    propertyName={propertyName}
                                                    loading
                                                />
                                                <MultiUnitLoader
                                                    multiUnitDataCount={4}
                                                />
                                            </Fragment>
                                        );
                                    }

                                    setQueryEndTimer(
                                        'multiUnitSearchQuery_desktop'
                                    );

                                    if (error) {
                                        logger.captureException(
                                            `[MultiUnitSearchQuery error]: ${error}`,
                                            {
                                                extra: `Unable to fetch listing results from graphql-traveler-search for property Id - ${propertyId}`
                                            }
                                        );
                                    }

                                    return (
                                        <div>
                                            <MultiUnitTypesSummary
                                                propertyName={propertyName}
                                                resultCount={
                                                    data &&
                                                    data.multiUnitSearch &&
                                                    data.multiUnitSearch
                                                        .resultCount
                                                }
                                                loading={false}
                                            />
                                            <Timing measureName="multiUnitListing">
                                                <MultiUnitListing
                                                    context="dropdown"
                                                    fullLocale={fullLocale}
                                                    isDatelessSearch={
                                                        isDatelessSearch
                                                    }
                                                    isMetric={isMetric}
                                                    multiUnitList={data}
                                                    propertyId={propertyId}
                                                />
                                            </Timing>
                                        </div>
                                    );
                                }}
                            </Query>
                        ))}
                </Dropdown>
            </div>
        );
    }
}
